import React, { useState, useMemo, useRef, useEffect } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiDownload, FiCopy, FiCheck, FiX, FiAlertTriangle } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import Table from "components/ui/Table";
import FilterBar from "components/ui/FilterBar";
import {
  useAdminDepositsQuery,
  useDepositEventsQuery,
  useDepositLedgerQuery,
  useLedgerAccountsQuery,
  useFloatBalanceQuery,
  useAdminRetryPayoutMutation,
  useAdminRefundDepositMutation,
  useAdminCancelDepositMutation,
  useCreateDepositForPropertyMutation,
} from "apis/deposit";
import SearchSelect from "components/ui/SearchSelect";
import Button from "components/ui/Button";
import { useAuthSchedulesQuery } from "apis/authSchedule";

/* ────────────────────────────── Vocabulary ────────────────────────────── */

// Real pipeline statuses, grouped by colour. Key order = pipeline order.
const STATUS_META = {
  provisioning: {
    label: "Provisioning",
    className: "bg-lightgray text-darkgray",
  },
  provisioning_failed: {
    label: "Provisioning failed",
    className: "bg-red-100 text-red-800",
  },
  awaiting_payment: {
    label: "Awaiting payment",
    className: "bg-yellow-100 text-yellow-800",
  },
  funds_received: {
    label: "Funds received",
    className: "bg-blue-100 text-blue-800",
  },
  funds_cleared: {
    label: "Funds cleared",
    className: "bg-blue-100 text-blue-800",
  },
  routing: { label: "In progress", className: "bg-purple-100 text-purple-800" },
  settled: { label: "Completed", className: "bg-green-100 text-green-800" },
  payout_failed: { label: "Payout failed", className: "bg-red-100 text-red-800" },
  refunded: { label: "Refunded", className: "bg-lightgray text-darkgray" },
  cancelled: { label: "Cancelled", className: "bg-lightgray text-darkgray" },
};

const STATUS_ORDER = Object.keys(STATUS_META);

// Still live: not settled, refunded or cancelled. Note this is broader than
// the apis/deposit IN_FLIGHT_STATUSES, which exists to decide polling — these
// two lists mean different things and are deliberately kept apart.
const OPEN_STATUSES = [
  "provisioning",
  "awaiting_payment",
  "funds_received",
  "funds_cleared",
  "routing",
];

// States nobody will resolve without a human.
const ATTENTION_STATUSES = ["provisioning_failed", "payout_failed"];

const PERIOD_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const PIPELINE_STEPS = [
  "Provisioning",
  "Awaiting payment",
  "In float",
  "In progress",
  "Settled",
];

const STEP_INDEX = {
  provisioning: 0,
  provisioning_failed: 0,
  awaiting_payment: 1,
  funds_received: 2,
  funds_cleared: 2,
  refunded: 2,
  routing: 3,
  payout_failed: 3,
  settled: 4,
  cancelled: 0,
};

// Which rail the money arrived on. NPP is final on receipt; a direct credit
// can still be dishonoured for a few days, which is why it reads differently.
const RAIL_META = {
  npp: { label: "NPP / PayID", className: "bg-green-50 text-green-800 border-green-200" },
  direct_credit: {
    label: "Bank transfer",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  rtgs: { label: "RTGS", className: "bg-lightgray text-darkgray border-mediumgray" },
};

const CREDIT_STATUS_META = {
  cleared: { label: "Cleared", className: "text-green-700", dot: "bg-green-600" },
  received: { label: "Clearing", className: "text-amber-700", dot: "bg-amber-500" },
  returned: { label: "Dishonoured", className: "text-red-700", dot: "bg-red-500" },
};

// Human labels for timeline events written by the backend
const TIMELINE_LABELS = {
  sale_won: "Deposit created",
  // Timeline entries are never rewritten, so deposits created before the
  // sale/deal rename still carry the old key.
  deal_won: "Deposit created",
  account_created: "Receivable account created",
  provisioning_failed: "Provisioning failed",
  payid_registered: "PayID registered",
  payid_registration_failed: "PayID registration failed",
  awaiting_payment: "Account active — ready for payment",
  payment_recovered: "Payment recovered — webhook was never delivered",
  payment_details_sent: "Payment details emailed to buyer",
  payment_details_email_failed: "Could not email payment details",
  funds_received: "Funds received",
  funds_cleared: "Funds cleared in float",
  credit_returned: "Incoming payment dishonoured",
  payout_reference_generated: "Payout reference generated",
  payout_reference_cleared: "Payout reference reset for retry",
  payout_initiated: "Payout initiated",
  routing: "Routing to trust account",
  settled: "Settled to trust account",
  payout_rejected: "Payout rejected",
  payout_returned: "Payout returned to float",
  payout_status_unknown: "Unrecognised payout status",
  auto_route_failed: "Auto-route failed",
  refund_created: "Refund created",
  refund_failed: "Refund failed",
  account_disabled: "Account disabled",
  cancelled: "Deposit cancelled",
};

const humanizeEvent = (event) =>
  TIMELINE_LABELS[event] ||
  String(event || "")
    .replace(/_/g, " ")
    .replace(/payid/gi, "PayID")
    .replace(/npp/gi, "NPP")
    .replace(/^\w/, (c) => c.toUpperCase());

/* ────────────────────────────── Formatting ────────────────────────────── */

const formatMoney = (cents) => {
  if (cents === undefined || cents === null) return "-";
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
};

// Exact money for per-payment lines (shows cents only when present)
const formatMoneyExact = (cents) => {
  if (cents === undefined || cents === null) return "-";
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};


// Monoova transaction ids ("N00100000000", "D00100537784") and our own
// uniqueReference UUIDs, chipped inside free text so notes stay readable.
const renderNoteText = (text) => {
  const parts = String(text).split(
    /\b([ND]\d{9,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/gi,
  );
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <code
        key={i}
        className="text-[11px] bg-lightgray border border-mediumgray rounded px-1 py-0.5 mx-0.5 whitespace-nowrap"
      >
        {part}
      </code>
    ) : (
      part
    ),
  );
};

const timelineNote = (t) => {
  if (!t.note || t.note === TIMELINE_LABELS[t.event]) return null;
  return renderNoteText(t.note);
};

const eventNote = (note) => {
  if (!note) return "—";
  if (note === "Unhandled event type (stored only)") {
    return "Stored — no action needed";
  }
  return renderNoteText(note);
};

/* ────────────────────────── Presentational pieces ────────────────────────── */

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || {
    label: status,
    className: "bg-lightgray text-darkgray",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const RailBadge = ({ rail }) => {
  const meta = RAIL_META[rail] || RAIL_META.rtgs;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] whitespace-nowrap ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const SectionTitle = ({ children, right }) => (
  <div className="flex items-baseline justify-between mb-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-darkgray">
      {children}
    </h4>
    {right}
  </div>
);

const DetailRow = ({ label, children }) => (
  <div className="flex justify-between gap-6 py-2 border-b border-mediumgray last:border-0">
    <span className="text-sm text-darkgray shrink-0">{label}</span>
    <span className="text-sm text-right break-words min-w-0">{children}</span>
  </div>
);

// Copy button with inline tooltip feedback (no toast) — icon flips to a
// check and a small "Copied" bubble appears beside it for 1.5s
const CopyButton = ({ text, label }) => {
  const [state, setState] = useState("idle"); // idle | copied | failed
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    clearTimeout(timerRef.current);
    try {
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch (e) {
      setState("failed");
    }
    timerRef.current = setTimeout(() => setState("idle"), 1500);
  };

  return (
    <span className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={handleCopy}
        className="p-1.5 rounded hover:bg-lightgray text-darkgray hover:text-black transition-colors"
        aria-label={`Copy ${label}`}
        title={`Copy ${label}`}
      >
        {state === "copied" ? (
          <FiCheck size={15} className="text-green-700" />
        ) : (
          <FiCopy size={15} />
        )}
      </button>
      {state !== "idle" && (
        <span
          className={`absolute right-full mr-1.5 top-1/2 -translate-y-1/2 whitespace-nowrap rounded px-2 py-0.5 text-[11px] text-white pointer-events-none ${
            state === "copied" ? "bg-black" : "bg-red-600"
          }`}
          role="status"
        >
          {state === "copied" ? "Copied" : "Copy failed"}
        </span>
      )}
    </span>
  );
};

// The 5-step pipeline rail — shows at a glance where the money is
const PipelineRail = ({ status }) => {
  const idx = STEP_INDEX[status] ?? 0;
  const isFailed = ATTENTION_STATUSES.includes(status);
  const isMuted = status === "refunded" || status === "cancelled";

  return (
    <div className="flex items-start">
      {PIPELINE_STEPS.map((label, i) => {
        const done = i < idx || (i === idx && status === "settled");
        const current = i === idx && !done;
        const failedHere = current && isFailed;

        let circle;
        if (failedHere) {
          circle = (
            <span className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center">
              <FiX size={13} />
            </span>
          );
        } else if (done) {
          circle = (
            <span
              className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${
                isMuted ? "bg-mediumgray" : "bg-black"
              }`}
            >
              <FiCheck size={13} />
            </span>
          );
        } else if (current) {
          circle = (
            <span className="w-6 h-6 rounded-full border-2 border-black bg-white flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-black" />
            </span>
          );
        } else {
          circle = (
            <span className="w-6 h-6 rounded-full border border-mediumgray bg-white" />
          );
        }

        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className={`flex-1 h-px mt-3 ${
                  i <= idx && !isMuted ? "bg-black" : "bg-mediumgray"
                }`}
              />
            )}
            <div className="flex flex-col items-center w-16 shrink-0">
              {circle}
              <span
                className={`mt-1.5 text-[11px] leading-tight text-center ${
                  current ? "font-semibold text-black" : "text-darkgray"
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Red / neutral context banner for terminal or failed states. Each one says
// what actually happened to the money and what a human should do next.
const StateBanner = ({ deposit }) => {
  const box = (tone, children) => (
    <div
      className={
        tone === "red"
          ? "rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
          : "rounded-md border border-mediumgray bg-lightgray px-3 py-2.5 text-sm text-darkgray"
      }
    >
      {children}
    </div>
  );

  if (deposit.status === "payout_failed") {
    // With one payout per credit, the deposit is flagged by whichever one is
    // rejected — that is the failure worth explaining.
    const f = (deposit.payouts || []).find(
      (payout) => payout.status === "rejected" || payout.status === "returned",
    )?.failure;
    const reason = f?.reason || f?.status || f?.statusDescription;
    return box(
      "red",
      <>
        <span className="font-medium">Payout failed</span>
        {reason ? ` — ${reason}` : ""}. The funds are back in the float account.
        Fix the agency's trust account details, then retry the payout.
      </>,
    );
  }
  if (deposit.status === "provisioning_failed") {
    return box(
      "red",
      <>
        <span className="font-medium">Provisioning failed</span> — the buyer was
        never given an account to pay into. Create a new deposit for this sale.
      </>,
    );
  }
  if (deposit.status === "provisioning") {
    return box(
      "grey",
      <>
        The receivable account is being enabled for NPP. This normally takes a
        few minutes.{" "}
        <span className="font-medium">
          Do not send payment details to the buyer yet
        </span>{" "}
        — a payment made now would be returned.
      </>,
    );
  }
  if (deposit.status === "refunded") {
    return box("grey", "This deposit was refunded to the buyer.");
  }
  if (deposit.status === "cancelled") {
    return box(
      "grey",
      "This deposit was cancelled — the account no longer accepts payment.",
    );
  }
  return null;
};

// KPI card. `tone: "alert"` turns it red when the value is non-zero, so a
// number that needs a human reads differently from one that does not.
/*
 * Provisions a deposit — and with it a payable PayID — for a property before
 * any buyer exists, so the PayID is ready the moment a sale happens.
 *
 * The backend does the real gatekeeping (live/sold only, one per property, a
 * verified trust account must exist). This form deliberately does NOT
 * re-implement those rules; it shows whatever the server refuses with. The
 * property list is the same source AdminOverrides uses.
 */
const ProvisionDeposit = () => {
  const [propertyId, setPropertyId] = useState("");
  const { data: schedules = [], isPending: loadingProps } =
    useAuthSchedulesQuery("address,propertyId");
  const { mutate: create, isPending: creating } =
    useCreateDepositForPropertyMutation({
      onSuccess: () => setPropertyId(""),
    });

  // Settled listings are past the point of taking a deposit, so they are not
  // offered — the backend would refuse them anyway.
  const options = useMemo(
    () =>
      schedules
        .filter((s) => s.address && !s.isSettled)
        .map((s) => ({
          value: s.propertyId,
          label: s.address,
          subLabel: s.organizationName || "",
        })),
    [schedules],
  );
  const selected = options.find((o) => o.value === propertyId) || null;

  return (
    <div className="border border-mediumgray rounded-md p-4 mb-6">
      <p className="text-sm font-semibold">Provision a deposit PayID</p>
      <p className="text-xs text-darkgray mt-1 mb-3">
        Creates the Monoova account and PayID for a property ahead of the buyer.
        The listing must be live or sold. The buyer and the agreed amount are
        filled in later, at contract review and sign — until the amount is set,
        any money that arrives is held rather than paid to the trust account.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 min-w-0">
          <SearchSelect
            options={options}
            value={selected}
            onChange={(opt) => setPropertyId(opt?.value || "")}
            placeholder="Select a property"
            isLoading={loadingProps}
            isClearable
          />
        </div>
        <Button
          className="black-button whitespace-nowrap"
          disabled={!propertyId || creating}
          loading={creating}
          onClick={() => create(propertyId)}
        >
          Create deposit
        </Button>
      </div>
    </div>
  );
};

// How an Automatcher stands against our own records. Only "ok" is nothing to
// look at; the other three each mean something different went wrong.
const LEDGER_HEALTH = {
  ok: { label: "Linked", className: "bg-green-50 text-green-800" },
  orphan: { label: "No deposit record", className: "bg-red-100 text-red-800" },
  missing: { label: "Not at Monoova", className: "bg-red-100 text-red-800" },
  closed: { label: "Closed", className: "bg-lightgray text-darkgray" },
};

const LedgerHealthBadge = ({ health }) => {
  const meta = LEDGER_HEALTH[health] || LEDGER_HEALTH.ok;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[11px] whitespace-nowrap ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const TABS = [
  { id: "payments", label: "Payments" },
  { id: "payids", label: "PayIDs" },
];

const TabButton = ({ tab, isActive, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(tab.id)}
    role="tab"
    aria-selected={isActive}
    className={`whitespace-nowrap py-2 px-2.5 border-b-2 font-medium text-sm transition-colors ${
      isActive
        ? "border-black text-black"
        : "border-transparent text-darkgray hover:text-darkergray hover:border-lightgray"
    }`}
  >
    {tab.label}
  </button>
);

/*
 * Every PayID we hold, reconciled against what Monoova holds.
 *
 * MONOOVA HAS NO PAYID LISTING. Verified against production, both candidates:
 * /ledger/v1/allAccounts returns seven fields and none is a PayID, and
 * /receivables/v1/statusByClientID returns bsb/account/isActive only. So our
 * own Deposit records ARE the register — Monoova cannot be asked "what PayIDs
 * do I have". The ledger listing is still worth pulling, because a ledger with
 * no deposit behind it is money we would otherwise never see.
 */
const PayIds = () => {
  const { data: ledgers = [], isPending } = useLedgerAccountsQuery();

  /*
   * ONE row per Automatcher, joined both ways, so the list is the whole truth
   * rather than a healthy table beside a banner of exceptions.
   *
   *   ok       Monoova holds it and we have the deposit
   *   orphan   Monoova holds it, we have no deposit — money can be sitting in
   *            it and nothing of ours will route or refund it. Usually a test
   *            deposit deleted from our side, which leaves the ledger behind.
   *   missing  we have the deposit, Monoova returned no ledger — provisioning
   *            failed. The opposite problem, and it must not read as an orphan.
   *   closed   Monoova has closed it; kept for history, not actionable.
   */
  const rows = useMemo(
    () =>
      ledgers.map((l) => ({
        ...l,
        health: l.isClosed
          ? "closed"
          : l.source === "beleef"
            ? "missing"
            : l.deposit
              ? "ok"
              : "orphan",
      })),
    [ledgers],
  );

  const problems = rows.filter((r) => r.health === "orphan" || r.health === "missing");

  const columns = useMemo(
    () => [
      {
        accessorKey: "deposit.payid",
        header: "PayID",
        cell: (row) => {
          if (row.deposit?.payid) {
            return (
              <span className="inline-flex items-center gap-1.5">
                <code className="text-xs break-all">{row.deposit.payid}</code>
                <CopyButton text={row.deposit.payid} label="PayID" />
              </span>
            );
          }
          // With the deposit gone, the PayID is UNKNOWABLE: we no longer hold
          // it and Monoova has no PayID read or list endpoint (five paths
          // probed, all 404). "Unknown" not "none" — a PayID may well still be
          // live against this account, and the account takes money by
          // BSB/account either way.
          if (!row.deposit) {
            return (
              <span className="text-xs text-red-800">Unknown — still live</span>
            );
          }
          return (
            <span className="text-xs text-darkgray">
              {row.deposit.bankAccountNumber ? "Provisioning…" : "—"}
            </span>
          );
        },
        isFilter: true,
        minWidth: "230px",
      },
      {
        accessorKey: "deposit.bankTransfer.accountNumber",
        header: "Bank account",
        cell: (row) =>
          row.deposit?.bankTransfer ? (
            <code className="text-xs whitespace-nowrap">
              {row.deposit.bankTransfer.bsb} ·{" "}
              {row.deposit.bankTransfer.accountNumber}
            </code>
          ) : (
            <span className="text-xs text-darkgray">—</span>
          ),
        isFilter: true,
        minWidth: "160px",
      },
      {
        accessorKey: "health",
        header: "State",
        cell: (row) => <LedgerHealthBadge health={row.health} />,
        isFilter: true,
      },
      {
        accessorKey: "deposit.propertyAddress",
        header: "Property",
        cell: (row) =>
          row.deposit?.propertyAddress || (
            <span className="text-xs text-darkgray">
              {/* The ref is the saleId of the deposit that used to exist — the
                  only trace of what an orphan was for. */}
              {row.ledgerUniqueReference
                ? `ref ${String(row.ledgerUniqueReference).slice(0, 12)}`
                : "—"}
            </span>
          ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "deposit.organizationName",
        header: "Agency",
        cell: (row) => row.deposit?.organizationName || "—",
        isFilter: true,
      },
      {
        accessorKey: "deposit.status",
        header: "Deposit",
        cell: (row) =>
          row.deposit ? (
            <StatusBadge status={row.deposit.status} />
          ) : (
            <span className="text-xs text-darkgray">No record</span>
          ),
        isFilter: true,
      },
      {
        accessorKey: "deposit.buyerName",
        header: "Buyer",
        // Empty on a PayID provisioned ahead of the sale — the point of the
        // pre-provisioning flow, not missing data.
        cell: (row) =>
          row.deposit?.buyerName ||
          (row.deposit ? (
            <span className="text-xs text-darkgray">Not yet assigned</span>
          ) : (
            "—"
          )),
      },
      {
        accessorKey: "ledgerAccountNumber",
        header: "Automatcher",
        cell: (row) => (
          <code className="text-xs">{row.ledgerAccountNumber || "—"}</code>
        ),
        minWidth: "150px",
      },
      {
        accessorKey: "createdDateTime",
        header: "Created",
        cell: (row) => formatDate(row.createdDateTime || row.deposit?.createdAt),
      },
    ],
    [],
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      <ProvisionDeposit />

      {problems.length > 0 && (
        <div className="mb-6 border border-red-200 bg-red-50 rounded-md p-4 flex items-start gap-2">
          <FiAlertTriangle size={14} className="text-red-700 mt-0.5 shrink-0" />
          <p className="text-xs text-red-800">
            <span className="font-semibold">
              {problems.length} of {rows.length} need attention.
            </span>{" "}
            Orphaned Automatchers are still ACTIVE at Monoova and can still take
            money — by BSB and account number, and by any PayID still registered
            against them. Nothing of ours would route, receipt or refund it.
            Usually a deposit deleted on our side, which leaves the Monoova
            account behind. Closing them at Monoova is the only way to make them
            stop accepting payment.
          </p>
        </div>
      )}

      <Table
        title="Automatchers"
        columns={columns}
        data={rows}
        pageSize={10}
        rowClassName={(row) =>
          row.health === "orphan" || row.health === "missing"
            ? "bg-red-50"
            : row.health === "closed"
              ? "text-darkgray"
              : ""
        }
        emptyText="No Automatchers yet — provision one above"
        searchPlaceholder="Search by PayID, property, agency or account…"
      />
    </>
  );
};

const KpiCard = ({ label, value, sub, tone, loading }) => {
  const alert = tone === "alert" && value !== 0 && value !== "-";
  return (
    <div
      className={`border rounded-lg p-5 ${
        alert ? "border-red-200 bg-red-50" : "border-mediumgray"
      }`}
    >
      <p
        className={`text-xs uppercase tracking-wide mb-1 ${
          alert ? "text-red-700" : "text-darkgray"
        }`}
      >
        {label}
      </p>
      <p
        className={`text-2xl font-semibold ${alert ? "text-red-800" : ""} ${
          loading ? "text-mediumgray" : ""
        }`}
      >
        {loading ? "—" : value}
      </p>
      {sub && (
        <p
          className={`text-xs mt-1 ${alert ? "text-red-700" : "text-darkgray"}`}
        >
          {sub}
        </p>
      )}
    </div>
  );
};

// Where the buyer sends money: the PayID, and the BSB and account number the
// buyer email gives as the alternative. Both reach the same account, so either
// is matched to this sale.
const PaymentInstructions = ({ deposit }) => {
  const m = deposit.monoova || {};
  const bank = deposit.bankTransfer;
  const hasAccount = Boolean(m.bankAccountNumber);
  const ready = deposit.status === "awaiting_payment" && m.isActive;

  if (!hasAccount) {
    return (
      <div className="rounded-md border border-mediumgray bg-lightgray px-3 py-2.5">
        <p className="text-[11px] uppercase tracking-wide text-darkgray">
          Pay to
        </p>
        <p className="text-sm mt-0.5">Not provisioned yet</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border px-3 py-2.5 space-y-2.5 ${
        ready ? "border-mediumgray bg-lightgray" : "border-mediumgray bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-darkgray">
          Pay to
        </p>
        {!ready && (
          <span className="text-[11px] text-amber-700">Not payable yet</span>
        )}
      </div>

      {m.payid && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] text-darkgray">PayID</p>
            <code className="text-sm break-all">{m.payid}</code>
          </div>
          <CopyButton text={m.payid} label="PayID" />
        </div>
      )}

      {bank && (
        <>
          <div>
            <p className="text-[11px] text-darkgray">Account name</p>
            <p className="text-sm">{bank.accountName}</p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] text-darkgray">BSB</p>
              <code className="text-sm">
                {bank.bsb}
                {bank.bsbInstitution ? ` (${bank.bsbInstitution})` : ""}
              </code>
            </div>
            <CopyButton text={bank.bsb} label="BSB" />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] text-darkgray">Account number</p>
              <code className="text-sm">{bank.accountNumber}</code>
            </div>
            <CopyButton text={bank.accountNumber} label="Account number" />
          </div>
        </>
      )}
    </div>
  );
};

/*
 * Actions that move real money, so each one states its consequence and asks
 * for confirmation inline rather than firing on a single click.
 *
 * Availability mirrors the backend's own guards exactly — a button that is
 * shown must be a button that works:
 *   retry  — funds_cleared | payout_failed, and something cleared to route
 *   refund — not routing/settled, and at least one cleared credit
 *   cancel — not routing/settled/cancelled/refunded
 */
const DepositActions = ({ deposit, onDone }) => {
  const [pending, setPending] = useState(null); // null | "refund" | "cancel"
  const [reason, setReason] = useState("");

  const retry = useAdminRetryPayoutMutation();
  const refund = useAdminRefundDepositMutation();
  const cancel = useAdminCancelDepositMutation();

  const busy = retry.isPending || refund.isPending || cancel.isPending;

  const availableCents =
    (deposit.totalClearedCents || 0) - (deposit.totalRefundedCents || 0);
  const hasClearedCredit = (deposit.credits || []).some(
    (c) => c.status === "cleared",
  );

  const canRetry =
    ["funds_cleared", "payout_failed"].includes(deposit.status) &&
    availableCents > 0;
  const canRefund =
    !["routing", "settled"].includes(deposit.status) && hasClearedCredit;
  const canCancel = !["routing", "settled", "cancelled", "refunded"].includes(
    deposit.status,
  );

  if (!canRetry && !canRefund && !canCancel) return null;

  const close = () => {
    setPending(null);
    setReason("");
  };

  const run = (mutation, vars) =>
    mutation.mutate(vars, {
      onSuccess: () => {
        close();
        onDone?.();
      },
    });

  const confirmCopy =
    pending === "refund"
      ? {
          title: `Refund ${formatMoneyExact(availableCents)} to the buyer?`,
          body: "Monoova returns the funds to the account the payment came from. This cannot be undone.",
          cta: "Refund buyer",
          onConfirm: () =>
            run(refund, { id: deposit._id, reason: reason || undefined }),
        }
      : {
          title: "Cancel this deposit?",
          body: "The receivable account is deactivated. Any further payment to it is rejected and returned to the sender.",
          cta: "Cancel deposit",
          onConfirm: () =>
            run(cancel, { id: deposit._id, reason: reason || undefined }),
        };

  return (
    <div className="border-t border-mediumgray pt-4">
      {!pending ? (
        <div className="flex flex-wrap items-center gap-2">
          {canRetry && (
            <button
              type="button"
              className="black-button"
              disabled={busy}
              onClick={() => run(retry, deposit._id)}
            >
              {retry.isPending ? "Retrying…" : "Retry payout"}
            </button>
          )}
          {canRefund && (
            <button
              type="button"
              className="px-4 py-2 rounded border border-mediumgray text-sm hover:bg-lightgray disabled:opacity-50"
              disabled={busy}
              onClick={() => setPending("refund")}
            >
              Refund buyer
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              className="px-4 py-2 rounded border border-mediumgray text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              disabled={busy}
              onClick={() => setPending("cancel")}
            >
              Cancel deposit
            </button>
          )}
          {canRetry && deposit.status === "payout_failed" && (
            <span className="text-xs text-darkgray">
              Check the agency's trust account details first.
            </span>
          )}
        </div>
      ) : (
        <div className="rounded-md border border-mediumgray bg-lightgray p-3 space-y-3">
          <div>
            <p className="text-sm font-medium">{confirmCopy.title}</p>
            <p className="text-xs text-darkgray mt-0.5">{confirmCopy.body}</p>
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, recorded on the timeline)"
            className="w-full border border-mediumgray rounded px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="black-button"
              disabled={busy}
              onClick={confirmCopy.onConfirm}
            >
              {busy ? "Working…" : confirmCopy.cta}
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded border border-mediumgray text-sm hover:bg-white"
              disabled={busy}
              onClick={close}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────── The page ────────────────────────────── */

const DepositAuditReport = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [selectedId, setSelectedId] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Top-of-page filters (client-side over the loaded rows)
  const [statusFilter, setStatusFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");

  const pageSize = 15;

  const {
    data: depositsResult,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useAdminDepositsQuery();

  const { data: float, isPending: isFloatPending, isError: isFloatError } =
    useFloatBalanceQuery();

  const deposits = depositsResult?.data || [];

  const { data: audit } = useDepositEventsQuery(selectedId, {
    enabled: !!selectedId && isViewOpen,
  });

  // 409 for a pre-ledger deposit, whose money sits in the pooled float — the
  // panel simply does not render for those.
  const { data: ledger } = useDepositLedgerQuery(selectedId, {
    enabled: !!selectedId && isViewOpen,
  });

  const statusOptions = useMemo(() => {
    const set = new Set();
    deposits.forEach((d) => d.status && set.add(d.status));
    return [...set]
      .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
      .map((value) => ({ value, label: STATUS_META[value]?.label || value }));
  }, [deposits]);

  const agencyOptions = useMemo(() => {
    const map = new Map();
    deposits.forEach((d) => {
      const id = d.organizationId?._id || d.organizationId;
      const name = d.organizationId?.name;
      if (id && name) map.set(String(id), name);
    });
    return [...map].map(([value, label]) => ({ value, label }));
  }, [deposits]);

  const filteredData = useMemo(() => {
    const cutoff = periodFilter
      ? Date.now() - Number(periodFilter) * 24 * 60 * 60 * 1000
      : null;
    return deposits.filter((d) => {
      if (statusFilter && d.status !== statusFilter) return false;
      if (agencyFilter) {
        const id = String(d.organizationId?._id || d.organizationId || "");
        if (id !== agencyFilter) return false;
      }
      if (cutoff && new Date(d.createdAt).getTime() < cutoff) return false;
      return true;
    });
  }, [deposits, statusFilter, agencyFilter, periodFilter]);

  const hasActiveFilters = statusFilter || agencyFilter || periodFilter;

  const clearFilters = () => {
    setStatusFilter("");
    setAgencyFilter("");
    setPeriodFilter("");
  };

  // Summary follows the active filters, except the float balance, which is a
  // live figure from Monoova and cannot be filtered.
  const summary = useMemo(() => {
    const settled = filteredData.filter((d) => d.status === "settled");
    const open = filteredData.filter((d) => OPEN_STATUSES.includes(d.status));
    const attention = filteredData.filter((d) =>
      ATTENTION_STATUSES.includes(d.status),
    );
    const agencyIds = new Set(
      settled
        .map((d) => d.organizationId?._id || d.organizationId)
        .filter(Boolean),
    );
    return {
      settledCents: settled.reduce(
        (sum, d) =>
          sum +
          ((d.payouts || [])
            .filter((payout) => payout.status === "successful")
            .reduce((total, payout) => total + (payout.amountCents || 0), 0) ||
            d.totalClearedCents ||
            0),
        0,
      ),
      settledCount: settled.length,
      agencyCount: agencyIds.size,
      // Money actually with us. awaiting_payment contributes nothing — no
      // payment has arrived — so it is counted but never inflates the figure.
      inFlightCents: open.reduce(
        (sum, d) => sum + (d.totalReceivedCents || 0),
        0,
      ),
      inFlightCount: open.length,
      attentionCount: attention.length,
      awaitingCount: filteredData.filter((d) => d.status === "awaiting_payment")
        .length,
    };
  }, [filteredData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
        minWidth: "44px",
      },
      {
        accessorKey: "propertyId.address",
        header: "Property",
        cell: (row) => (
          <div className="min-w-0">
            <div className="font-medium truncate">
              {row.propertyId?.address || "-"}
            </div>
            <div className="text-[11px] text-darkgray truncate">
              {row.organizationId?.name || "-"}
            </div>
          </div>
        ),
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "buyer.name",
        header: "Buyer",
        cell: (row) => (
          <div className="min-w-0">
            <div className="text-sm truncate">{row.buyer?.name || "-"}</div>
            <div className="text-[11px] text-darkgray truncate">
              {row.buyer?.email || ""}
            </div>
          </div>
        ),
        isFilter: true,
        minWidth: "160px",
      },
      {
        accessorKey: "monoova.payid",
        header: "Pay to",
        cell: (row) => {
          const m = row.monoova || {};
          // An account with no PayID yet is still provisioning — say so rather
          // than showing a dash, which reads as "nothing here".
          if (!m.payid) {
            return (
              <span className="text-xs text-darkgray">
                {m.bankAccountNumber ? "Provisioning…" : "—"}
              </span>
            );
          }
          const bank = row.bankTransfer;
          return (
            <>
              <code className="block text-[11px] text-darkgray truncate" title={m.payid}>
                {m.payid}
              </code>
              {bank && (
                <code className="block text-[11px] text-darkgray truncate">
                  BSB {bank.bsb} · {bank.accountNumber}
                </code>
              )}
            </>
          );
        },
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "expectedAmountCents",
        header: "Expected",
        cell: (row) => (
          <span className="text-sm font-medium whitespace-nowrap">
            {formatMoney(row.expectedAmountCents)}
          </span>
        ),
        exportValue: (row) => (row.expectedAmountCents || 0) / 100,
        isFilter: false,
        minWidth: "110px",
      },
      {
        accessorKey: "totalReceivedCents",
        header: "Received",
        cell: (row) => {
          const received = row.totalReceivedCents || 0;
          const expected = row.expectedAmountCents || 0;
          const short = received > 0 && received < expected;
          const over = received > expected;
          return (
            <div className="whitespace-nowrap">
              <span
                className={`text-sm ${
                  short ? "text-amber-700 font-medium" : ""
                } ${over ? "text-blue-700 font-medium" : ""}`}
              >
                {received ? formatMoney(received) : "—"}
              </span>
              {short && (
                <span className="block text-[11px] text-amber-700">
                  Short {formatMoney(expected - received)}
                </span>
              )}
              {over && (
                <span className="block text-[11px] text-blue-700">
                  Over {formatMoney(received - expected)}
                </span>
              )}
            </div>
          );
        },
        exportValue: (row) => (row.totalReceivedCents || 0) / 100,
        isFilter: false,
        minWidth: "120px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => <StatusBadge status={row.status} />,
        exportValue: (row) => STATUS_META[row.status]?.label || row.status,
        isFilter: false,
        minWidth: "140px",
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-sm whitespace-nowrap">
            {formatDate(row.createdAt)}
          </span>
        ),
        exportValue: (row) => formatDate(row.createdAt),
        isFilter: false,
        minWidth: "110px",
      },
    ],
    [],
  );

  const handleExportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = [
      "Sale ID",
      "Property",
      "Buyer",
      "Buyer email",
      "Agency",
      "Created",
      "PayID",
      "Account name",
      "BSB",
      "Account number",
      "Expected (AUD)",
      "Received (AUD)",
      "Cleared (AUD)",
      "Refunded (AUD)",
      "Status",
      "Payouts",
      "Payouts settled",
    ];
    const rows = filteredData.map((d) =>
      [
        d.saleId,
        d.propertyId?.address,
        d.buyer?.name,
        d.buyer?.email,
        d.organizationId?.name,
        formatDate(d.createdAt),
        d.monoova?.payid,
        d.bankTransfer?.accountName,
        d.bankTransfer?.bsb,
        d.bankTransfer?.accountNumber,
        (d.expectedAmountCents || 0) / 100,
        (d.totalReceivedCents || 0) / 100,
        (d.totalClearedCents || 0) / 100,
        (d.totalRefundedCents || 0) / 100,
        STATUS_META[d.status]?.label || d.status,
        // One row per deposit, so many payouts collapse into counts.
        (d.payouts || []).length,
        (d.payouts || []).filter((p) => p.status === "successful").length,
      ]
        .map(esc)
        .join(","),
    );
    const blob = new Blob([[header.map(esc).join(","), ...rows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `deposit-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading deposits: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedDeposit = deposits.find((d) => d._id === selectedId);

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const ExportCsvButton = () => (
    <button
      className="black-button flex items-center"
      onClick={handleExportCsv}
      disabled={!filteredData.length}
    >
      <FiDownload size={14} className="mr-2" />
      Export CSV
    </button>
  );

  // An unverified webhook means the signature check did not pass. Surfaced
  // because it is the difference between "Monoova told us this" and "someone
  // told us this".
  const unverifiedCount = (audit?.events || []).filter(
    (e) => e.signatureValid === false,
  ).length;

  return (
    <>
      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Deposit details"
        className="max-w-2xl"
      >
        {selectedDeposit && (
          <div className="space-y-6">
            {/* Header: amount, context, status */}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-3xl font-semibold leading-none">
                  {formatMoney(selectedDeposit.expectedAmountCents)}
                </p>
                <p className="text-sm text-darkgray mt-1.5 truncate">
                  {selectedDeposit.propertyId?.address ||
                    selectedDeposit.buyer?.name}
                  {selectedDeposit.organizationId?.name
                    ? ` · ${selectedDeposit.organizationId.name}`
                    : ""}
                </p>
              </div>
              <StatusBadge status={selectedDeposit.status} />
            </div>

            <PipelineRail status={selectedDeposit.status} />

            <StateBanner deposit={selectedDeposit} />

            <PaymentInstructions deposit={selectedDeposit} />

            {/* Money at a glance */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["Expected", selectedDeposit.expectedAmountCents],
                ["Received", selectedDeposit.totalReceivedCents],
                ["Cleared", selectedDeposit.totalClearedCents],
                ["Refunded", selectedDeposit.totalRefundedCents],
              ].map(([label, cents]) => (
                <div
                  key={label}
                  className="rounded-md border border-mediumgray px-3 py-2.5"
                >
                  <p className="text-[11px] uppercase tracking-wide text-darkgray">
                    {label}
                  </p>
                  <p className="text-base font-semibold mt-0.5">
                    {formatMoney(cents || 0)}
                  </p>
                </div>
              ))}
            </div>

            {/* Payments received — the per-payment detail. Buyers split
                payments and overpay, so this is never assumed to be one row. */}
            <div>
              <SectionTitle>
                Payments received
                {selectedDeposit.credits?.length
                  ? ` (${selectedDeposit.credits.length})`
                  : ""}
              </SectionTitle>
              {!selectedDeposit.credits?.length ? (
                <p className="text-xs text-darkgray">
                  No payments received yet.
                </p>
              ) : (
                <div className="border border-mediumgray rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-lightgray text-left text-[11px] uppercase tracking-wide text-darkgray">
                        <th className="px-3 py-2 font-medium whitespace-nowrap">
                          Received
                        </th>
                        <th className="px-3 py-2 font-medium">From</th>
                        <th className="px-3 py-2 font-medium">Rail</th>
                        <th className="px-3 py-2 font-medium text-right whitespace-nowrap">
                          Amount
                        </th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDeposit.credits.map((c, i) => {
                        const meta =
                          CREDIT_STATUS_META[c.status] ||
                          CREDIT_STATUS_META.received;
                        return (
                          <tr
                            key={c.transactionId || i}
                            className="border-t border-mediumgray align-top"
                          >
                            <td className="px-3 py-2 text-xs text-darkgray whitespace-nowrap">
                              {formatDateTime(c.receivedAt)}
                              {c.transactionId && (
                                <code className="block text-[10px] mt-0.5">
                                  {c.transactionId}
                                </code>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              <div className="font-medium text-black">
                                {c.payer?.name || "Unknown payer"}
                              </div>
                              {c.payer?.description && (
                                <div className="text-darkgray break-words">
                                  {c.payer.description}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <RailBadge rail={c.rail} />
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap font-medium">
                              {formatMoneyExact(c.amountCents)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-1.5 text-xs ${meta.className}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}
                                />
                                {meta.label}
                              </span>
                              {c.status === "received" && c.clearsAt && (
                                <span className="block text-[10px] text-darkgray mt-0.5">
                                  clears {formatDate(c.clearsAt)}
                                </span>
                              )}
                              {c.status === "returned" && c.returnReason && (
                                <span className="block text-[10px] text-red-700 mt-0.5">
                                  {c.returnReason}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payout legs — one per cleared credit, not one per deposit. */}
            {selectedDeposit.payouts?.length > 0 && (
              <div>
                <SectionTitle>
                  Payouts to trust account ({selectedDeposit.payouts.length})
                </SectionTitle>
                {selectedDeposit.payouts.map((payout) => (
                  <div
                    key={payout.uniqueReference}
                    className="border-b border-lightgray py-2 last:border-0"
                  >
                    <DetailRow label="Amount">
                      {formatMoneyExact(payout.amountCents)}
                      {payout.feeCents
                        ? ` · fee ${formatMoneyExact(payout.feeCents)}`
                        : ""}
                    </DetailRow>
                    <DetailRow label="Status">
                      {payout.status || "-"}
                      {payout.rail ? ` · ${payout.rail}` : ""}
                    </DetailRow>
                    <DetailRow label="For credit">
                      <code className="text-xs">
                        {payout.creditTransactionId || "-"}
                      </code>
                    </DetailRow>
                    <DetailRow label="Transaction">
                      {payout.transactionId ? (
                        <code className="text-xs">{payout.transactionId}</code>
                      ) : (
                        <span className="text-darkgray">Not dispatched</span>
                      )}
                    </DetailRow>
                    <DetailRow label="Initiated">
                      {formatDateTime(payout.initiatedAt)}
                    </DetailRow>
                    {payout.settledAt && (
                      <DetailRow label="Settled">
                        {formatDateTime(payout.settledAt)}
                      </DetailRow>
                    )}
                    {payout.receiptSentAt && (
                      <DetailRow label="Receipt sent">
                        {formatDateTime(payout.receiptSentAt)}
                      </DetailRow>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Refunds */}
            {selectedDeposit.refunds?.length > 0 && (
              <div>
                <SectionTitle>
                  Refunds ({selectedDeposit.refunds.length})
                </SectionTitle>
                <div className="border border-mediumgray rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-lightgray text-left text-[11px] uppercase tracking-wide text-darkgray">
                        <th className="px-3 py-2 font-medium">Requested</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                        <th className="px-3 py-2 font-medium text-right">
                          Amount
                        </th>
                        <th className="px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedDeposit.refunds.map((r, i) => (
                        <tr
                          key={r.uniqueReference || i}
                          className="border-t border-mediumgray align-top"
                        >
                          <td className="px-3 py-2 text-xs text-darkgray whitespace-nowrap">
                            {formatDateTime(r.requestedAt)}
                          </td>
                          <td className="px-3 py-2 text-xs break-words">
                            {r.reason || "—"}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap font-medium">
                            {formatMoneyExact(r.amountCents)}
                          </td>
                          <td
                            className={`px-3 py-2 text-xs whitespace-nowrap ${
                              r.status === "failed" ? "text-red-700" : ""
                            }`}
                          >
                            {r.status}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Details */}
            <div>
              <SectionTitle>Details</SectionTitle>
              <DetailRow label="Buyer">
                {selectedDeposit.buyer?.name}
                <span className="text-darkgray">
                  {" "}
                  · {selectedDeposit.buyer?.email}
                </span>
              </DetailRow>
              <DetailRow label="Agency">
                {selectedDeposit.organizationId?.name || "-"}
              </DetailRow>
              <DetailRow label="Property">
                {selectedDeposit.propertyId?.address || "-"}
              </DetailRow>
              <DetailRow label="Sale ID">
                <span className="inline-flex items-center gap-1.5">
                  <code className="text-xs">{selectedDeposit.saleId}</code>
                  <CopyButton text={selectedDeposit.saleId} label="Sale ID" />
                </span>
              </DetailRow>
              <DetailRow label="Created">
                {formatDateTime(selectedDeposit.createdAt)}
              </DetailRow>
            </div>

            {/* Timeline */}
            <div>
              <SectionTitle>Timeline</SectionTitle>
              <div>
                {(selectedDeposit.timeline || []).map((t, i, arr) => {
                  const note = timelineNote(t);
                  return (
                    <div key={i} className="relative pl-6 pb-4 last:pb-0">
                      {i !== arr.length - 1 && (
                        <span
                          className="absolute left-[5px] top-4 bottom-0 w-px bg-mediumgray"
                          aria-hidden="true"
                        />
                      )}
                      <span className="absolute left-0 top-1 w-[11px] h-[11px] rounded-full border-2 border-black bg-white" />
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-medium">
                          {humanizeEvent(t.event)}
                        </span>
                        <span className="text-xs text-darkgray">
                          {formatDateTime(t.at)}
                        </span>
                      </div>
                      {note && (
                        <p className="text-xs text-darkgray mt-0.5 break-words">
                          {note}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/*
              Monoova's own balance for this sale, against ours. A difference is
              the only visible sign of a webhook we never received — otherwise a
              deposit that missed a payment looks exactly like an unpaid one.
            */}
            {ledger && (
              <div>
                <SectionTitle>Ledger reconciliation</SectionTitle>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <p className="text-darkgray">Monoova holds</p>
                    <p className="font-semibold tabular-nums">
                      {formatMoney(ledger.balanceCents)}
                    </p>
                  </div>
                  <div>
                    <p className="text-darkgray">We recorded</p>
                    <p className="font-semibold tabular-nums">
                      {formatMoney(ledger.ourClearedCents)}
                    </p>
                  </div>
                  <div>
                    <p className="text-darkgray">Difference</p>
                    <p
                      className={`font-semibold tabular-nums ${
                        ledger.differenceCents === 0
                          ? "text-green-700"
                          : "text-red-700"
                      }`}
                    >
                      {formatMoney(ledger.differenceCents)}
                    </p>
                  </div>
                </div>
                {ledger.differenceCents !== 0 && (
                  <p className="mt-2 text-[11px] text-red-700">
                    Monoova and our records disagree — a webhook was probably
                    missed. The inbound reconciliation cron recovers payments
                    from the last two days.
                  </p>
                )}
                <p className="mt-2 text-[11px] text-darkgray">
                  Ledger {ledger.ledgerAccountNumber} · automatcher{" "}
                  {ledger.automatcherBankAccountNumber || "—"}
                  {ledger.isClosed ? " · CLOSED" : ""}
                </p>
              </div>
            )}

            {/* Webhook events */}
            <div>
              <SectionTitle
                right={
                  unverifiedCount > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-red-700">
                      <FiAlertTriangle size={12} />
                      {unverifiedCount} unverified signature
                      {unverifiedCount === 1 ? "" : "s"}
                    </span>
                  ) : null
                }
              >
                Webhook events{audit ? ` (${audit.eventCount})` : ""}
              </SectionTitle>
              {!audit ? (
                <p className="text-xs text-darkgray">Loading events…</p>
              ) : audit.events?.length === 0 ? (
                <p className="text-xs text-darkgray">No webhook events yet</p>
              ) : (
                <div className="border border-mediumgray rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-lightgray text-left text-[11px] uppercase tracking-wide text-darkgray">
                        <th className="px-3 py-2 font-medium whitespace-nowrap">
                          Time
                        </th>
                        <th className="px-3 py-2 font-medium">Event</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium w-full">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.events.map((e) => (
                        <tr
                          key={e._id}
                          className="border-t border-mediumgray align-top"
                        >
                          <td className="px-3 py-2 text-xs text-darkgray whitespace-nowrap">
                            {formatDateTime(e.createdAt)}
                          </td>
                          <td className="px-3 py-2">
                            <code className="text-[11px] whitespace-nowrap">
                              {e.eventName}
                            </code>
                            {e.signatureValid === false && (
                              <span
                                className="block text-[10px] text-red-700"
                                title="The Verification-Signature did not check out"
                              >
                                unverified
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1.5 text-xs ${
                                e.processed ? "text-green-700" : "text-red-600"
                              }`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  e.processed ? "bg-green-600" : "bg-red-500"
                                }`}
                              />
                              {e.processed ? "Processed" : "Unprocessed"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-darkgray break-words">
                            {eventNote(e.note || e.processingError)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <DepositActions
              deposit={selectedDeposit}
              onDone={() => setIsViewOpen(false)}
            />
          </div>
        )}
      </Modal>

      <div className="container mx-auto">
        {/* Payments is the money view; PayIDs is the register of what can be
            paid INTO, which is provisioned separately and ahead of any sale. */}
        <div className="border-b border-lightgray mb-6">
          <nav className="-mb-px flex overflow-auto" role="tablist">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                tab={tab}
                isActive={activeTab === tab.id}
                onClick={setActiveTab}
              />
            ))}
          </nav>
        </div>

        {activeTab === "payids" && <PayIds />}

        {activeTab === "payments" && (
          <>
        {/* KPIs. Float is live from Monoova; the rest follow the filters. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="Held in float"
            value={
              isFloatError
                ? "Unavailable"
                : formatMoney(float?.actualBalanceCents)
            }
            sub={
              isFloatError
                ? "Could not reach Monoova"
                : float?.accountNumber
                  ? `mAccount ${float.accountNumber}`
                  : "Buyer funds not yet routed"
            }
            loading={isFloatPending}
          />
          <KpiCard
            label="Settled to trust"
            value={formatMoney(summary.settledCents)}
            sub={`${summary.settledCount} deposits · ${summary.agencyCount} ${
              summary.agencyCount === 1 ? "agency" : "agencies"
            }`}
          />
          <KpiCard
            label="In flight"
            value={formatMoney(summary.inFlightCents)}
            sub={`${summary.inFlightCount} open · ${summary.awaitingCount} awaiting payment`}
          />
          <KpiCard
            label="Needs attention"
            value={summary.attentionCount}
            sub="Provisioning or payout failures"
            tone="alert"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-end items-center gap-3 mb-4">
          <FilterBar
            showClear={hasActiveFilters}
            onClear={clearFilters}
            filters={[
              {
                key: "status",
                value: statusFilter,
                onChange: setStatusFilter,
                allLabel: "All Statuses",
                options: statusOptions,
                minWidth: "180px",
              },
              {
                key: "agency",
                value: agencyFilter,
                onChange: setAgencyFilter,
                allLabel: "All Agencies",
                options: agencyOptions,
                minWidth: "200px",
              },
              {
                key: "period",
                value: periodFilter,
                onChange: setPeriodFilter,
                allLabel: "All Time",
                options: PERIOD_OPTIONS,
                minWidth: "150px",
              },
            ]}
          />
        </div>

        <Table
          title="Sales Deposit Audit Report"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          ButtonCmp={ExportCsvButton}
          onRowClick={handleViewRowClick}
          emptyText="No deposits found"
          searchPlaceholder="Search by property, buyer, account or PayID…"
        />
          </>
        )}
      </div>
    </>
  );
};

export default DepositAuditReport;
